// Chart Audit — single-page progressive review workflow (redesigned 2026-09-12).
// Three-screen flow (batch list -> patient list -> patient audit) merged into one
// nested accordion. Engine untouched: same endpoints, same human disposition and
// director sign-off acts. Discharge Audit strip absorbed from EMR.js, retokenized.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { tokens as tk } from './theme';

const API = 'https://tcio-api.onrender.com';

// Glyphs — escape-composed, never raw (paste-mangling doctrine)
const DOT = '\u00B7';
const CHECK = '\u2713';
const ARROW = '\u2192';
const LARROW = '\u2190';
const CHEV_D = '\u25BE';
const CHEV_R = '\u25B8';
const SPARK = '\u2726';
const NDASH = '\u2013';

const AI_FG = tk.purple || tk.blue700;
const AI_BG = tk.purpleSoft || tk.aiSoft;

const N = v => Number(v) || 0;
const fmtD = v => v ? String(v).slice(0, 10) : '';
const fmtShort = v => v ? new Date(v).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'UTC' }) : '-';
const areaLabel = a => (a || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const AV_COLORS = [tk.blue600, tk.blue700, tk.ai, tk.success, tk.warning, tk.blue400];
const avColor = name => AV_COLORS[(String(name || '').split('').reduce((s, c) => s + c.charCodeAt(0), 0)) % AV_COLORS.length];
function Avatar({ name, size = 26, dim }) {
  const init = String(name || '?').split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return <span style={{ width: size, height: size, borderRadius: '50%', background: dim ? tk.borderSoft : avColor(name),
    color: dim ? tk.textMuted : tk.surface, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.38, fontWeight: 700, flexShrink: 0 }}>{init}</span>;
}

const badge = (bg, fg, text, key) => <span key={key} style={{ background: bg, color: fg, borderRadius: 7,
  padding: '2px 9px', fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-block' }}>{text}</span>;

const SEV_META = {
  critical: { bg: tk.dangerBg, fg: tk.danger, text: 'Critical' },
  warning:  { bg: tk.warningBg, fg: tk.warning, text: 'Warning' },
  info:     { bg: tk.aiSoft, fg: tk.ai, text: 'Info' },
};
const DISPO_META = {
  open:           { bg: tk.dangerBg, fg: tk.danger, text: 'Open' },
  resolved:       { bg: tk.successBg, fg: tk.success, text: 'Resolved' },
  accepted_risk:  { bg: tk.warningBg, fg: tk.warning, text: 'Accepted Risk' },
  not_applicable: { bg: tk.surfaceSoft, fg: tk.textMuted, text: 'N/A' },
};

const s = {
  card: { background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow },
  label: { fontSize: 10.5, letterSpacing: '1px', textTransform: 'uppercase', color: tk.textMuted, fontWeight: 700 },
  select: { borderRadius: tk.radiusControl, padding: '7px 10px', font: 'inherit', fontSize: 12.5, fontWeight: 600,
    border: `1px solid ${tk.border}`, background: tk.surface, color: tk.ink, cursor: 'pointer' },
  th: { textAlign: 'left', padding: '9px 12px', fontSize: 10.5, letterSpacing: '1px', textTransform: 'uppercase',
    color: tk.textMuted, fontWeight: 700, background: tk.surfaceSoft, borderBottom: `1px solid ${tk.borderSoft}` },
  td: { padding: '10px 12px', fontSize: 13, color: tk.ink, borderBottom: `1px solid ${tk.borderSoft}` },
};
const btnPrimary = { borderRadius: tk.radiusControl, padding: '9px 16px', font: 'inherit', fontWeight: 700, fontSize: 13,
  cursor: 'pointer', background: tk.ai, color: tk.surface, border: 'none' };
const btnBlue = { borderRadius: tk.radiusControl, padding: '8px 14px', font: 'inherit', fontWeight: 600, fontSize: 12.5,
  cursor: 'pointer', background: tk.surface, color: tk.blue700, border: `1px solid ${tk.blue600}` };
const btnGhost = { borderRadius: tk.radiusControl, padding: '6px 12px', font: 'inherit', fontWeight: 600, fontSize: 12,
  cursor: 'pointer', background: 'transparent', color: tk.textSecondary, border: `1px solid ${tk.border}` };

function useMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 760);
  useEffect(() => {
    const f = () => setM(window.innerWidth < 760);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  return m;
}

function Skeleton({ h = 14, w = '100%', mb = 8 }) {
  return <div className="caSkel" style={{ height: h, width: w, marginBottom: mb, borderRadius: 6, background: tk.borderSoft }} />;
}
const SkelStyle = () => <style>{'@keyframes caPulse{0%{opacity:.45}50%{opacity:.9}100%{opacity:.45}}.caSkel{animation:caPulse 1.2s ease-in-out infinite}'}</style>;

// ---- Cloned documentation (ticket 1a634a32): notes whose narrative matches another note by the same author or for the same client ----
function ClonedDocs({ onOpenPatient }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState('open');
  const [open, setOpen] = useState(false);
  const [cmp, setCmp] = useState({});      // flagId -> { note, matched } | 'loading'
  const [busy, setBusy] = useState({});
  const load = useCallback(() => {
    setErr('');
    axios.get(`${API}/api/emr/note-similarity`, { params: { days, status } })
      .then(r => setData(r.data))
      .catch(() => setErr('Unable to load cloned-documentation audit.'));
  }, [days, status]);
  useEffect(() => { load(); }, [load]);
  const compare = (id) => {
    if (cmp[id]) { setCmp(c => { const n = { ...c }; delete n[id]; return n; }); return; }
    setCmp(c => ({ ...c, [id]: 'loading' }));
    axios.get(`${API}/api/emr/note-similarity/${id}/compare`).then(r => setCmp(c => ({ ...c, [id]: r.data }))).catch(() => setCmp(c => ({ ...c, [id]: { error: true } })));
  };
  const setFlag = async (id, st) => {
    setBusy(b => ({ ...b, [id]: true }));
    try { await axios.patch(`${API}/api/emr/note-similarity/${id}`, { status: st }); load(); } catch (e) { alert('Update failed'); }
    setBusy(b => ({ ...b, [id]: false }));
  };
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  const typeLabel = (t) => ({ group_note: 'Group note', progress_note: 'Progress note', ur_review: 'UR review', treatment_plan: 'Treatment plan', treatment_plan_review: 'TP review', assessment: 'Assessment', case_mgmt_note: 'Case mgmt', discharge_summary: 'Discharge summary' })[t] || t;

  if (err) return <div style={{ ...s.card, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: tk.danger }}>
    {err} <button style={{ ...btnGhost, marginLeft: 8 }} onClick={load}>Retry</button></div>;
  if (!data) return null;
  const flags = data.flags || []; const sum = data.summary || {};
  return (
    <div style={{ ...s.card, marginBottom: 14, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 700 }}>{open ? '▾' : '▸'} Cloned documentation</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: (sum.open || 0) > 0 ? tk.danger : tk.success, background: (sum.open || 0) > 0 ? tk.dangerBg || '#FDECEC' : tk.successBg, borderRadius: 999, padding: '2px 9px' }}>{sum.open || 0} open</span>
        <span style={{ fontSize: 12, color: tk.textMuted }}>notes whose narrative is ≥{Math.round((data.threshold || 0.8) * 100)}% identical to another note by the same author or for the same client · last {data.window_days} days</span>
        <select value={status} onChange={e => { e.stopPropagation(); setStatus(e.target.value); }} onClick={e => e.stopPropagation()} style={{ ...s.select, marginLeft: 'auto' }}>
          <option value="open">Open</option><option value="reviewed">Reviewed</option><option value="dismissed">Dismissed</option><option value="all">All</option>
        </select>
        <select value={days} onChange={e => { e.stopPropagation(); setDays(Number(e.target.value)); }} onClick={e => e.stopPropagation()} style={s.select}>
          {[7, 30, 90, 180].map(d => <option key={d} value={d}>{d} days</option>)}
        </select>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${tk.borderSoft}` }}>
          {!flags.length && <div style={{ padding: '12px 14px', fontSize: 12.5, color: tk.textMuted }}>Nothing flagged.</div>}
          {flags.map(f => (
            <div key={f.id} style={{ borderTop: `1px solid ${tk.borderSoft}`, padding: '10px 14px', fontSize: 12.5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: f.similarity >= 0.95 ? tk.danger : tk.warning, minWidth: 44 }}>{Math.round(f.similarity * 100)}%</span>
                <span style={{ fontWeight: 600 }}>{typeLabel(f.note_type)}</span>
                <span style={{ color: tk.textMuted }}>{f.author_name || 'Unknown author'}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: tk.surfaceSoft, color: tk.textSecondary }}>
                  {f.same_session ? 'same group session' : f.scope === 'author' ? 'same author' : 'same client'}</span>
                <span>
                  <button style={{ ...btnGhost, padding: '1px 6px' }} onClick={() => onOpenPatient && onOpenPatient(f.patient_id)}>{f.patient_name}</button>
                  <span style={{ color: tk.textMuted }}> {fmtD(f.note_at)} ↔ </span>
                  <button style={{ ...btnGhost, padding: '1px 6px' }} onClick={() => onOpenPatient && onOpenPatient(f.matched_patient_id)}>{f.matched_patient_name}</button>
                  <span style={{ color: tk.textMuted }}> {fmtD(f.matched_note_at)}{f.matched_author_name && f.matched_author_name !== f.author_name ? ` · by ${f.matched_author_name}` : ''}</span>
                </span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button style={btnGhost} onClick={() => compare(f.id)}>{cmp[f.id] ? 'Hide' : 'Compare'}</button>
                  {f.status === 'open' && <button style={btnGhost} disabled={busy[f.id]} onClick={() => setFlag(f.id, 'reviewed')}>Reviewed</button>}
                  {f.status === 'open' && <button style={btnGhost} disabled={busy[f.id]} onClick={() => setFlag(f.id, 'dismissed')}>Dismiss</button>}
                  {f.status !== 'open' && <span style={{ fontSize: 11, color: tk.textMuted }}>{f.status}{f.reviewed_by_name ? ` by ${f.reviewed_by_name}` : ''}</span>}
                </span>
              </div>
              {cmp[f.id] && cmp[f.id] !== 'loading' && !cmp[f.id].error && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
                  {[['This note', cmp[f.id].note, f.patient_name], ['Matched note', cmp[f.id].matched, f.matched_patient_name]].map(([lbl, n, who]) => (
                    <div key={lbl} style={{ background: tk.surfaceSoft, borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: tk.textSecondary, marginBottom: 4 }}>{lbl} · {who} · {n.words} words</div>
                      <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: 1.45, maxHeight: 260, overflow: 'auto' }}>{n.text}</div>
                    </div>
                  ))}
                </div>
              )}
              {cmp[f.id] === 'loading' && <div style={{ marginTop: 8, color: tk.textMuted }}>Loading…</div>}
              {cmp[f.id] && cmp[f.id].error && <div style={{ marginTop: 8, color: tk.danger }}>Could not load the two notes.</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Discharge Audit strip (moved from EMR.js, behavior byte-faithful, retokenized) ----
function DischargeAudit({ onOpenPatient }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [days, setDays] = useState(90);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    setErr('');
    axios.get(`${API}/api/emr/discharge-audit`, { params: { days } })
      .then(r => setData(r.data))
      .catch(() => setErr('Unable to load discharge audit.'));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div style={{ ...s.card, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: tk.danger }}>
    {err} <button style={{ ...btnGhost, marginLeft: 8 }} onClick={load}>Retry</button></div>;
  if (!data) return null;
  const outstanding = data.rows.length > 0;
  return (
    <div style={{ ...s.card, marginBottom: 16, overflow: 'hidden' }}>
      <div onClick={() => outstanding && setOpen(o => !o)}
        style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px', flexWrap: 'wrap',
          cursor: outstanding ? 'pointer' : 'default' }}>
        {outstanding && <span style={{ color: tk.textMuted, fontSize: 12 }}>{open ? CHEV_D : CHEV_R}</span>}
        <span style={{ fontSize: 13.5, fontWeight: 700, color: tk.ink }}>Discharge Audit</span>
        {outstanding
          ? badge(tk.dangerBg, tk.danger, `${data.clients} client${N(data.clients) === 1 ? '' : 's'} with ${data.total_outstanding} outstanding item${N(data.total_outstanding) === 1 ? '' : 's'}`)
          : badge(tk.successBg, tk.success, `${CHECK} All discharged charts complete`)}
        <select value={days} onClick={e => e.stopPropagation()} onChange={e => setDays(Number(e.target.value))}
          title="Discharge audit window" style={{ ...s.select, marginLeft: 'auto' }}>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>
      {outstanding && open && (
        <div style={{ overflowX: 'auto', borderTop: `1px solid ${tk.borderSoft}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead><tr>
              <th style={s.th}>Client</th><th style={s.th}>Facility</th><th style={s.th}>Discharged</th>
              <th style={s.th}>Open notes</th><th style={s.th}>Blocking failures</th><th style={s.th}>DC summary</th><th style={s.th}>Outstanding</th>
            </tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.episode_id} onClick={() => onOpenPatient(r.patient_id)} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = tk.surfaceSoft}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ ...s.td, fontWeight: 600 }}>{r.last_name}, {r.first_name} <span style={{ color: tk.textMuted, fontWeight: 400 }}>{r.mrn || ''}</span></td>
                  <td style={s.td}>{r.facility_name}</td>
                  <td style={s.td}>{fmtShort(r.discharge_date)}</td>
                  <td style={s.td}>{N(r.open_notes) > 0 ? badge(tk.warningBg, tk.warning, String(r.open_notes)) : '-'}</td>
                  <td style={s.td}>{N(r.blocking_failures) > 0 ? badge(tk.dangerBg, tk.danger, String(r.blocking_failures)) : '-'}</td>
                  <td style={s.td}>{r.missing_summary ? badge(tk.dangerBg, tk.danger, 'Missing') : badge(tk.successBg, tk.success, 'Done')}</td>
                  <td style={{ ...s.td, fontWeight: 700 }}>{r.outstanding}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- One finding card ----
function FindingCard({ f, onDisposition }) {
  const sev = SEV_META[f.severity] || SEV_META.info;
  const dis = DISPO_META[f.disposition] || DISPO_META.open;
  const openF = f.disposition === 'open';
  return (
    <div style={{ ...s.card, padding: '14px 16px', marginBottom: 10,
      borderLeft: `3px solid ${openF ? sev.fg : tk.borderSoft}`,
      opacity: f.disposition === 'not_applicable' ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {badge(sev.bg, sev.fg, sev.text)}
          <span style={{ fontSize: 11, color: tk.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px' }}>
            {(f.area || '').replace(/_/g, ' ')}</span>
          {f.doc_ref && <span style={{ fontSize: 11.5, color: tk.ai }}>doc {f.doc_ref}</span>}
        </div>
        {badge(dis.bg, dis.fg, dis.text)}
      </div>
      <div style={{ marginTop: 8, fontSize: 13.5, color: tk.ink, lineHeight: 1.55 }}>{f.issue}</div>
      {f.citation && <div style={{ marginTop: 6, fontSize: 12, color: tk.textMuted, fontStyle: 'italic' }}>{f.citation}</div>}
      {openF ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button style={btnBlue} onClick={() => onDisposition(f.id, 'resolved')}>Resolved</button>
          <button style={btnBlue} onClick={() => onDisposition(f.id, 'accepted_risk')}>Accept risk</button>
          <button style={btnBlue} onClick={() => onDisposition(f.id, 'not_applicable')}>N/A</button>
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: 12.5, color: tk.textSecondary }}>
          {dis.text} by {f.disposition_by_name} on {fmtD(f.disposition_at)}
          {f.disposition_note ? ` ${NDASH} ${f.disposition_note}` : ''}
          <button style={{ ...btnGhost, padding: '4px 10px', fontSize: 11.5, marginLeft: 10 }}
            onClick={() => onDisposition(f.id, 'open')}>Reopen</button>
        </div>
      )}
    </div>
  );
}

// ---- Expanded patient audit (the old third screen, inline) ----
function ExpandedAudit({ run, detail, loading, err, onRetry, onDisposition, onSignOff,
                         onPrev, onNext, hasPrev, nextLabel, mobile }) {
  const [note, setNote] = useState('');
  const [sort, setSort] = useState('severity');
  if (loading) return <div style={{ padding: '18px 16px' }}><Skeleton w="40%" h={18} /><Skeleton /><Skeleton w="85%" /><Skeleton w="60%" /></div>;
  if (err) return <div style={{ padding: '16px', fontSize: 13, color: tk.danger }}>
    Unable to load audit details. <button style={{ ...btnGhost, marginLeft: 6 }} onClick={onRetry}>Retry</button></div>;
  if (!detail) return null;
  const r = detail.run;
  const isError = r.status === 'error';
  const openCrit = detail.findings.filter(f => f.severity === 'critical' && f.disposition === 'open').length;
  const sevRank = { critical: 0, warning: 1, info: 2 };
  const findings = [...detail.findings].sort((a, b) => sort === 'status'
    ? ((a.disposition === 'open' ? 0 : 1) - (b.disposition === 'open' ? 0 : 1)) || (sevRank[a.severity] - sevRank[b.severity])
    : (sevRank[a.severity] - sevRank[b.severity]));
  return (
    <div style={{ padding: mobile ? '14px 12px' : '16px 18px', background: tk.surfaceSoft,
      borderTop: `1px solid ${tk.borderSoft}`, borderLeft: `3px solid ${tk.ai}` }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Avatar name={`${r.first_name} ${r.last_name}`} size={34} />
          <div>
            <span style={{ fontSize: 16, fontWeight: 700, color: tk.ink }}>{r.first_name} {r.last_name}</span>
            {r.reviewed_at
              ? <span style={{ marginLeft: 10 }}>{badge(tk.successBg, tk.success, 'Reviewed')}</span>
              : <span style={{ marginLeft: 10 }}>{badge(tk.warningBg, tk.warning, 'Needs Review')}</span>}
            <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 3 }}>
              Week {fmtD(r.week_start)} {NDASH} {fmtD(r.week_end)} {DOT} {N(r.documents_reviewed)} documents reviewed
              {r.therapist_name ? ` ${DOT} Therapist: ${r.therapist_name}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {badge(SEV_META.critical.bg, SEV_META.critical.fg, `${N(r.critical_count)} critical`)}
          {badge(SEV_META.warning.bg, SEV_META.warning.fg, `${N(r.warning_count)} warning`)}
          {badge(SEV_META.info.bg, SEV_META.info.fg, `${N(r.info_count)} info`)}
        </div>
      </div>
      {isError ? (
        <div style={{ ...s.card, padding: 14, marginTop: 14, fontSize: 13, color: tk.danger }}>
          Audit run failed: {r.error_text || 'unknown error'}
        </div>
      ) : (
      <div>
      {/* Full AI summary */}
      {r.summary && <div style={{ ...s.card, padding: '14px 16px', marginTop: 14, fontSize: 13.5, color: tk.ink, lineHeight: 1.6 }}>{r.summary}</div>}
      {/* Compact director sign-off */}
      <div style={{ marginTop: 14 }}>
        {r.reviewed_at ? (
          <div style={{ fontSize: 13, color: tk.success, fontWeight: 600 }}>
            {CHECK} Reviewed by {r.reviewed_by_name} on {fmtD(r.reviewed_at)}
            {r.review_note ? <span style={{ color: tk.textSecondary, fontWeight: 400 }}> {NDASH} {r.review_note}</span> : null}
          </div>
        ) : (
          <div>
            <div style={s.label}>Director sign-off</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={1}
                placeholder="Add review note (optional)"
                style={{ flex: '1 1 320px', minHeight: 36, resize: 'vertical', border: `1px solid ${tk.border}`,
                  borderRadius: tk.radiusControl, padding: '8px 11px', font: 'inherit', fontSize: 13, color: tk.ink, boxSizing: 'border-box' }} />
              <button style={btnPrimary} onClick={() => onSignOff(note)}>Sign Off Review</button>
            </div>
            {openCrit > 0 && <div style={{ fontSize: 12.5, color: tk.danger, fontWeight: 600, marginTop: 6 }}>
              {openCrit} critical finding{openCrit > 1 ? 's' : ''} still open</div>}
          </div>
        )}
      </div>
      {/* Findings */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: tk.ink }}>Audit Findings</div>
        <select value={sort} onChange={e => setSort(e.target.value)} style={s.select}>
          <option value="severity">Sort by: Severity</option>
          <option value="status">Sort by: Open first</option>
        </select>
      </div>
      {findings.length === 0 && <div style={{ fontSize: 13, color: tk.textSecondary }}>No findings recorded for this run.</div>}
      {findings.map(f => <FindingCard key={f.id} f={f} onDisposition={onDisposition} />)}
      </div>
      )}
      {/* Prev / Next */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        {hasPrev && <button style={btnBlue} onClick={onPrev}>{LARROW} Previous Patient</button>}
        <button style={btnPrimary} onClick={onNext}>{nextLabel} {ARROW}</button>
      </div>
    </div>
  );
}

// ---- Main page ----
export default function ChartAudit({ user, onOpenPatient }) {
  const mobile = useMobile();
  const [batches, setBatches] = useState(null);
  const [batchErr, setBatchErr] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [runsCache, setRunsCache] = useState({});
  const [runsLoading, setRunsLoading] = useState({});
  const [runsErr, setRunsErr] = useState({});
  const [openRunId, setOpenRunId] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const loadBatches = useCallback(async () => {
    setBatchErr('');
    try {
      const rows = (await axios.get(`${API}/api/emr/chart-audit/batches`)).data;
      setBatches(rows);
      return rows;
    } catch (e) { setBatchErr('Unable to load audit batches.'); return null; }
  }, []);

  const fetchRuns = useCallback(async (bid) => {
    setRunsLoading(m => ({ ...m, [bid]: true })); setRunsErr(m => ({ ...m, [bid]: '' }));
    try {
      const rows = (await axios.get(`${API}/api/emr/chart-audit/batches/${bid}`)).data;
      setRunsCache(m => ({ ...m, [bid]: rows }));
    } catch (e) { setRunsErr(m => ({ ...m, [bid]: 'Unable to load charts for this batch.' })); }
    setRunsLoading(m => ({ ...m, [bid]: false }));
  }, []);

  const fetchDetail = useCallback(async (rid, silent) => {
    if (!silent) { setDetailLoading(true); setDetailErr(''); }
    try {
      const d = (await axios.get(`${API}/api/emr/chart-audit/runs/${rid}`)).data;
      setDetailCache(m => ({ ...m, [rid]: d }));
    } catch (e) { setDetailErr('load'); }
    setDetailLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      const rows = await loadBatches();
      if (rows && rows.length) {
        setExpanded(new Set([rows[0].batch_id]));
        fetchRuns(rows[0].batch_id);
      }
    })();
  }, [loadBatches, fetchRuns]);

  const toggleBatch = (bid) => {
    setExpanded(prev => {
      const nx = new Set(prev);
      if (nx.has(bid)) { nx.delete(bid); }
      else { nx.add(bid); if (!runsCache[bid]) fetchRuns(bid); }
      return nx;
    });
  };
  const expandAll = () => {
    if (!batches) return;
    setExpanded(new Set(batches.map(b => b.batch_id)));
    batches.forEach(b => { if (!runsCache[b.batch_id]) fetchRuns(b.batch_id); });
  };
  const collapseAll = () => { setExpanded(new Set()); setOpenRunId(null); };

  const scrollToRun = (rid) => setTimeout(() => {
    const el = document.getElementById(`ca-run-${rid}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 80);

  const openRun = (rid) => {
    if (openRunId === rid) { setOpenRunId(null); return; }
    setOpenRunId(rid);
    if (!detailCache[rid]) fetchDetail(rid);
    scrollToRun(rid);
  };

  const kickBatch = async () => {
    if (!window.confirm('Run the weekly chart audit across the active census now? This audits every active patient chart with AI.')) return;
    setBusy(true); setMsg('');
    try {
      const r = (await axios.post(`${API}/api/emr/chart-audit/run-weekly`, {})).data;
      setMsg(`Batch started ${NDASH} ${r.patients} patients, week ${r.week_start} to ${r.week_end}. Refresh in a few minutes.`);
    } catch (e) { setMsg('Run failed: ' + (e.response?.data?.error || e.message)); }
    setBusy(false);
    loadBatches();
  };

  const disposition = async (bid, rid, findingId, d) => {
    let note = null;
    if (d !== 'open') { note = window.prompt('Disposition note (optional):') || null; }
    try {
      await axios.post(`${API}/api/emr/chart-audit/findings/${findingId}/disposition`, { disposition: d, note });
      fetchDetail(rid, true); fetchRuns(bid);
    } catch (e) { setMsg('Disposition failed: ' + (e.response?.data?.error || e.message)); }
  };

  const signOff = async (bid, rid, note) => {
    if (!window.confirm('Sign off this chart review as Clinical Director? This attests you have reviewed the findings.')) return;
    try {
      await axios.post(`${API}/api/emr/chart-audit/runs/${rid}/review`, { note: note || null });
      fetchDetail(rid, true); fetchRuns(bid); loadBatches();
    } catch (e) { setMsg('Sign-off failed: ' + (e.response?.data?.error || e.message)); }
  };

  const advance = (bid, rid, dir) => {
    const rows = runsCache[bid] || [];
    const idx = rows.findIndex(r => r.id === rid);
    if (idx < 0) return;
    if (dir < 0) {
      if (idx > 0) { const t = rows[idx - 1]; setOpenRunId(t.id); if (!detailCache[t.id]) fetchDetail(t.id); scrollToRun(t.id); }
      return;
    }
    const after = rows.slice(idx + 1).find(r => !r.reviewed_at && r.status !== 'error');
    const before = rows.slice(0, idx).find(r => !r.reviewed_at && r.status !== 'error');
    const target = after || before;
    if (target) { setOpenRunId(target.id); if (!detailCache[target.id]) fetchDetail(target.id); scrollToRun(target.id); }
    else setOpenRunId(null);
  };

  // ---- Derived header numbers (real data only) ----
  const list = batches || [];
  const totCharts = list.reduce((a, b) => a + N(b.runs), 0);
  const totNeed = list.reduce((a, b) => a + (N(b.runs) - N(b.reviewed)), 0);
  const totCrit = list.reduce((a, b) => a + N(b.criticals), 0);
  const showFacility = new Set(list.map(b => N(b.facility_count) > 1 ? 'multi' : (b.facility_name || ''))).size > 1;
  const facLabel = b => N(b.facility_count) > 1 ? 'Multiple facilities' : (b.facility_name || '');

  const gridCols = 'minmax(220px, 2.4fr) 64px 90px 90px 70px 130px 120px';

  return (
    <div>
      <SkelStyle />
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: tk.ink }}>Chart Audit</div>
          {batches !== null && list.length > 0 &&
            <div style={{ fontSize: 12.5, color: tk.textSecondary, marginTop: 4 }}>
              {list.length} audit batch{list.length === 1 ? '' : 'es'} {DOT} {totCharts} charts {DOT} {totNeed} need review {DOT} {totCrit} critical findings
            </div>}
        </div>
        <button style={{ ...btnPrimary, opacity: busy ? 0.7 : 1 }} disabled={busy} onClick={kickBatch}>
          {busy ? 'Starting' : 'Run weekly audit now'}</button>
      </div>
      {msg && <div style={{ ...s.card, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: tk.ink }}>{msg}</div>}
      <DischargeAudit onOpenPatient={onOpenPatient} />
      <ClonedDocs onOpenPatient={onOpenPatient} />

      {/* Weekly batches */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: tk.ink }}>Weekly Chart Audits</div>
        {list.length > 1 && (expanded.size < list.length
          ? <button style={btnGhost} onClick={expandAll}>Expand all</button>
          : <button style={btnGhost} onClick={collapseAll}>Collapse all</button>)}
      </div>
      {batchErr && <div style={{ ...s.card, padding: 14, fontSize: 13, color: tk.danger }}>
        {batchErr} <button style={{ ...btnGhost, marginLeft: 8 }} onClick={loadBatches}>Retry</button></div>}
      {batches === null && !batchErr && <div style={{ ...s.card, padding: 16 }}><Skeleton w="35%" /><Skeleton w="55%" /><Skeleton w="45%" mb={0} /></div>}
      {batches !== null && list.length === 0 && !batchErr &&
        <div style={{ ...s.card, padding: 18, fontSize: 13.5, color: tk.textSecondary }}>
          No audit batches yet. Run the first weekly audit to build the director review queue.</div>}

      {list.map(b => {
        const bid = b.batch_id;
        const isOpen = expanded.has(bid);
        const rows = runsCache[bid] || [];
        const pct = N(b.runs) > 0 ? Math.round(100 * N(b.reviewed) / N(b.runs)) : 0;
        return (
          <div key={bid} style={{ ...s.card, marginBottom: 10, overflow: 'hidden' }}>
            {/* Batch header row */}
            <div onClick={() => toggleBatch(bid)} style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: mobile ? '12px 12px' : '12px 16px', cursor: 'pointer', flexWrap: 'wrap' }}
              onMouseEnter={e => e.currentTarget.style.background = tk.surfaceSoft}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <span style={{ color: tk.textMuted, fontSize: 12 }}>{isOpen ? CHEV_D : CHEV_R}</span>
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: tk.ink }}>Week {fmtD(b.week_start)} {NDASH} {fmtD(b.week_end)}</span>
                <span style={{ fontSize: 12.5, color: tk.textSecondary, marginLeft: 8 }}>{b.runs} charts</span>
                {b.started_at && <span style={{ fontSize: 11.5, color: tk.textMuted, marginLeft: 8 }}>Run {new Date(b.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
                {showFacility && facLabel(b) && <span style={{ fontSize: 12, color: tk.blue700, fontWeight: 600, marginLeft: 8 }}>{facLabel(b)}</span>}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginLeft: mobile ? 0 : 'auto' }}>
                {N(b.errors) > 0 && badge(tk.dangerBg, tk.danger, `${b.errors} errors`)}
                {badge(SEV_META.critical.bg, SEV_META.critical.fg, `${N(b.criticals)} critical`)}
                {badge(SEV_META.warning.bg, SEV_META.warning.fg, `${N(b.warnings)} warning`)}
                {badge(SEV_META.info.bg, SEV_META.info.fg, `${N(b.infos)} info`)}
                {badge(N(b.reviewed) === N(b.runs) ? tk.successBg : tk.warningBg,
                       N(b.reviewed) === N(b.runs) ? tk.success : tk.warning,
                       `${b.reviewed}/${b.runs} reviewed`)}
              </div>
              {b.top_risk_area && !mobile &&
                <div style={{ fontSize: 11.5, color: tk.textMuted, textAlign: 'right', minWidth: 130 }}>
                  Highest risk:<br /><span style={{ color: tk.danger, fontWeight: 700 }}>{areaLabel(b.top_risk_area)}</span></div>}
            </div>
            {N(b.reviewed) > 0 && <div style={{ height: 3, background: tk.borderSoft }}>
              <div style={{ height: 3, width: `${pct}%`, background: tk.success }} /></div>}

            {isOpen && (
              <div style={{ borderTop: `1px solid ${tk.borderSoft}` }}>
                {/* AI summary strip — real math only */}
                {b.top_risk_area && N(b.top_risk_criticals) > 0 &&
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 16px',
                    background: AI_BG, borderBottom: `1px solid ${tk.borderSoft}`, fontSize: 12.5, flexWrap: 'wrap' }}>
                    <span style={{ color: AI_FG, fontWeight: 700 }}>{SPARK} TCIO Audit Summary:</span>
                    <span style={{ color: tk.ink }}>{areaLabel(b.top_risk_area)} accounts for {N(b.top_risk_criticals)} of {N(b.criticals)} critical findings in this batch.</span>
                  </div>}
                {runsLoading[bid] && !rows.length && <div style={{ padding: 16 }}><Skeleton /><Skeleton w="80%" /><Skeleton w="65%" mb={0} /></div>}
                {runsErr[bid] && <div style={{ padding: 14, fontSize: 13, color: tk.danger }}>
                  {runsErr[bid]} <button style={{ ...btnGhost, marginLeft: 8 }} onClick={() => fetchRuns(bid)}>Retry</button></div>}

                {rows.length > 0 && !mobile && (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: gridCols, background: tk.surfaceSoft,
                      borderBottom: `1px solid ${tk.borderSoft}` }}>
                      {['Patient', 'Docs', 'Critical', 'Warning', 'Open', 'Status', 'Actions'].map(h =>
                        <div key={h} style={{ ...s.th, background: 'transparent', borderBottom: 'none' }}>{h}</div>)}
                    </div>
                    {rows.map(r => {
                      const sel = openRunId === r.id;
                      const rev = !!r.reviewed_at;
                      const isErr = r.status === 'error';
                      return (
                        <div key={r.id} id={`ca-run-${r.id}`}>
                          <div onClick={() => openRun(r.id)} style={{ display: 'grid', gridTemplateColumns: gridCols,
                            alignItems: 'center', cursor: 'pointer', background: sel ? tk.aiSoft : 'transparent',
                            borderBottom: `1px solid ${tk.borderSoft}`, opacity: rev && !sel ? 0.75 : 1 }}
                            onMouseEnter={e => { if (!sel) e.currentTarget.style.background = tk.surfaceSoft; }}
                            onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                            <div style={{ ...s.td, borderBottom: 'none', display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                              <Avatar name={`${r.first_name} ${r.last_name}`} dim={rev} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: tk.ink }}>{rev ? `${CHECK} ` : ''}{r.first_name} {r.last_name}</div>
                                {r.summary && <div style={{ fontSize: 11.5, color: tk.textMuted, overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.summary}</div>}
                              </div>
                            </div>
                            <div style={{ ...s.td, borderBottom: 'none' }}>{N(r.documents_reviewed)}</div>
                            <div style={{ ...s.td, borderBottom: 'none' }}>{N(r.critical_count) > 0 ? badge(SEV_META.critical.bg, SEV_META.critical.fg, String(r.critical_count)) : '-'}</div>
                            <div style={{ ...s.td, borderBottom: 'none' }}>{N(r.warning_count) > 0 ? badge(SEV_META.warning.bg, SEV_META.warning.fg, String(r.warning_count)) : '-'}</div>
                            <div style={{ ...s.td, borderBottom: 'none' }}>{N(r.open_findings) > 0 ? badge(tk.aiSoft, tk.ai, String(r.open_findings)) : '-'}</div>
                            <div style={{ ...s.td, borderBottom: 'none' }}>
                              {isErr ? badge(tk.dangerBg, tk.danger, 'Error')
                                : rev ? badge(tk.successBg, tk.success, 'Reviewed')
                                : badge(tk.warningBg, tk.warning, 'Needs Review')}
                            </div>
                            <div style={{ ...s.td, borderBottom: 'none' }}>
                              <button style={sel ? btnBlue : { ...btnBlue, color: tk.ai, borderColor: tk.ai }}
                                onClick={e => { e.stopPropagation(); openRun(r.id); }}>
                                {sel ? 'Hide details' : `Review ${ARROW}`}</button>
                            </div>
                          </div>
                          {sel && <ExpandedAudit run={r} detail={detailCache[r.id]} loading={detailLoading && !detailCache[r.id]}
                            err={detailErr && !detailCache[r.id] ? detailErr : ''} onRetry={() => fetchDetail(r.id)}
                            onDisposition={(fid, d) => disposition(bid, r.id, fid, d)}
                            onSignOff={(note) => signOff(bid, r.id, note)}
                            onPrev={() => advance(bid, r.id, -1)} onNext={() => advance(bid, r.id, 1)}
                            hasPrev={rows.findIndex(x => x.id === r.id) > 0}
                            nextLabel="Save & Next Patient" mobile={mobile} key={r.id} />}
                        </div>
                      );
                    })}
                  </div>
                )}

                {rows.length > 0 && mobile && rows.map(r => {
                  const sel = openRunId === r.id;
                  const rev = !!r.reviewed_at;
                  return (
                    <div key={r.id} id={`ca-run-${r.id}`} style={{ borderBottom: `1px solid ${tk.borderSoft}` }}>
                      <div onClick={() => openRun(r.id)} style={{ padding: '12px 14px', cursor: 'pointer',
                        background: sel ? tk.aiSoft : 'transparent', opacity: rev && !sel ? 0.75 : 1 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <Avatar name={`${r.first_name} ${r.last_name}`} dim={rev} />
                          <div style={{ fontWeight: 600, color: tk.ink, fontSize: 14 }}>{rev ? `${CHECK} ` : ''}{r.first_name} {r.last_name}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                          {N(r.critical_count) > 0 && badge(SEV_META.critical.bg, SEV_META.critical.fg, `${r.critical_count} critical`)}
                          {N(r.warning_count) > 0 && badge(SEV_META.warning.bg, SEV_META.warning.fg, `${r.warning_count} warning`)}
                          {N(r.open_findings) > 0 && badge(tk.aiSoft, tk.ai, `${r.open_findings} open`)}
                          {r.status === 'error' ? badge(tk.dangerBg, tk.danger, 'Error')
                            : rev ? badge(tk.successBg, tk.success, 'Reviewed') : badge(tk.warningBg, tk.warning, 'Needs Review')}
                        </div>
                      </div>
                      {sel && <ExpandedAudit run={r} detail={detailCache[r.id]} loading={detailLoading && !detailCache[r.id]}
                        err={detailErr && !detailCache[r.id] ? detailErr : ''} onRetry={() => fetchDetail(r.id)}
                        onDisposition={(fid, d) => disposition(bid, r.id, fid, d)}
                        onSignOff={(note) => signOff(bid, r.id, note)}
                        onPrev={() => advance(bid, r.id, -1)} onNext={() => advance(bid, r.id, 1)}
                        hasPrev={rows.findIndex(x => x.id === r.id) > 0}
                        nextLabel="Save & Next" mobile={mobile} key={r.id} />}
                    </div>
                  );
                })}

                {!runsLoading[bid] && !runsErr[bid] && rows.length === 0 &&
                  <div style={{ padding: 16, fontSize: 13, color: tk.textSecondary }}>No charts in this batch.</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
